import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
const FALLBACK_HERO =
  "https://customer-assets.emergentagent.com/job_ritual-store-4/artifacts/2jk118j8_BackgroundImage.jpeg";

export const DEFAULT_CONTENT = {
  // Images
  hero_image: FALLBACK_HERO,
  visit_image: FALLBACK_HERO,
  rituals_intro_image: FALLBACK_HERO,
  ritual_card_1_image: "https://images.pexels.com/photos/3775147/pexels-photo-3775147.jpeg",
  ritual_card_2_image: "https://images.pexels.com/photos/599268/pexels-photo-599268.jpeg",
  ritual_card_3_image: "https://images.pexels.com/photos/3775140/pexels-photo-3775140.jpeg",
  gallery_image_1: "",
  gallery_image_2: "",
  gallery_image_3: "",
  gallery_image_4: "",
  // Store
  store_address:
    "02, Thambu Chetty Palya Main Road, in front of St. Antony's Church, Brindavan Layout, TC Palya, Krishnarajapuram, Bengaluru — 560036",
  store_phone: "+91 80000 00000",
  store_hours: "Monday – Sunday · 10:00 AM – 8:00 PM",
  store_email: "hello@srishti.shop",
  map_lat: "13.0215528",
  map_lng: "77.6998658",
  // Brand
  brand_name: "Srishti",
  brand_tagline: "Gift & Religious Articles",
  // Hero
  hero_eyebrow: "A Bangalore Boutique · In service of devotion",
  hero_title_line1: "Sacred objects,",
  hero_title_line2: "quietly curated.",
  hero_subtitle:
    "Statues, rosaries, crucifixes and Christian gift hampers — gathered for prayer corners and hand-finished for keeping. Available for pickup at our TC Palya, Bangalore boutique.",
  hero_cta_primary: "Discover the Shop",
  hero_cta_secondary: "Read the Guides",
  // Categories
  categories_eyebrow: "Collections",
  categories_title: "Shop by devotion",
  // Featured
  featured_title: "Quietly admired",
  featured_subtitle:
    "Eight pieces our visitors return for — handpicked from this season's collection.",
  // Rituals
  rituals_eyebrow: "Sacrament & Gift Guides",
  rituals_title_line1: "Mark the moment.",
  rituals_title_line2: "Choose with grace.",
  rituals_subtitle:
    "Every sacrament asks for the right offering. Our journals walk you through the keepsakes, the symbolism, and the small rituals — so you arrive prepared.",
  ritual_card_1_eyebrow: "Sacrament Guide",
  ritual_card_1_title: "First Holy Communion",
  ritual_card_1_text:
    "A white rosary, a missal, a single candle. The first Eucharist deserves heirloom-grade keepsakes — a few quiet objects to hold the day.",
  ritual_card_2_eyebrow: "Sacrament Guide",
  ritual_card_2_title: "Baptism Welcome",
  ritual_card_2_text:
    "A christening candle, a baby crucifix, a soft prayer book — small offerings for a soul newly received into the Church.",
  ritual_card_3_eyebrow: "Festive Guide",
  ritual_card_3_title: "The Christmas Edit",
  ritual_card_3_text:
    "Olive wood nativity sets, advent candles, frankincense and myrrh — the season of arrival, gathered into a single hamper.",
  // Visit Section (home)
  visit_section_eyebrow: "Visit the boutique",
  visit_section_title: "Step into Srishti.",
  visit_section_paragraph:
    "Our TC Palya boutique is a quiet sanctuary of olive wood, brass and candlelight. Pick up your reservations, browse the unlisted shelves, or speak with us about Communion and Baptism gifts.",
  visit_section_cta: "Get Directions",
  // Trust strip
  trust_1_title: "Reserved Online",
  trust_1_subtitle: "Picked up in-store",
  trust_2_title: "Cash on Pickup",
  trust_2_subtitle: "No prepayment needed",
  trust_3_title: "Holy-Land Sourced",
  trust_3_subtitle: "Olive wood & beyond",
  trust_4_title: "Boutique-Grade",
  trust_4_subtitle: "Hand-checked pieces",
  // Visit page
  visit_page_eyebrow: "Visit the boutique",
  visit_page_title_part1: "Find us beside",
  visit_page_title_part2: "St. Antony's Church",
  visit_page_subtitle:
    "A quiet sanctuary on Thambu Chetty Palya Main Road — lined with olive wood, brass and the warm glow of votives. Stop by, browse the unlisted shelves, or pick up your online reservation.",
  visit_page_cta: "Open in Google Maps",
  gallery_eyebrow: "Inside the boutique",
  gallery_title: "A few quiet corners.",
  gallery_subtitle:
    "Photographs from the shop floor — statues, hampers and the everyday quiet of TC Palya.",
  map_eyebrow: "Get directions",
  map_title: "On the map.",
  map_paragraph:
    "We're directly opposite St. Antony's Church on TC Palya Main Road. Easy access by auto, bus or metro from KR Puram.",
  map_cta: "Open Larger Map",
  // Footer
  footer_tagline:
    "A Bangalore-rooted Christian boutique curating statues, rosaries, crucifixes and devotional gifts. Visit our store at TC Palya for personal selections, sacrament hampers and prayer-corner essentials.",
  // New Arrivals
  new_arrivals_eyebrow: "Just In",
  new_arrivals_title: "Newly arrived",
  new_arrivals_subtitle:
    "Fresh from our atelier — devotional pieces that arrived this week.",
};

const ContentContext = createContext({ content: DEFAULT_CONTENT, refresh: () => {} });

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);

  const refresh = useCallback(() => {
    api
      .get("/settings")
      .then((r) => setContent({ ...DEFAULT_CONTENT, ...r.data }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Keep the browser tab title in sync with the brand
  useEffect(() => {
    const title = `${content.brand_name} — ${content.brand_tagline}`;
    document.title = title;
    // Re-assert after Emergent's runtime script (if any) overrides it
    const t = setInterval(() => {
      if (document.title !== title) document.title = title;
    }, 1000);
    return () => clearInterval(t);
  }, [content.brand_name, content.brand_tagline]);

  return (
    <ContentContext.Provider value={{ content, refresh }}>{children}</ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
